import { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    IconButton,
    MenuItem,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { useAlert } from '../../core/AlertContext';
import {
    IRegistrationFieldDraft,
    RegistrationSelectionType,
} from '../../Interfaces/IEventRegistration';
import {
    useDeleteEventRegistrationForm,
    useEventRegistrationForm,
    useSaveEventRegistrationForm,
} from '../../hooks/eventRegistration';
import { parseBackendError } from '../backendError';
import {
    dangerButtonSx,
    fieldSx,
    GREEN_800,
    primaryButtonSx,
    secondaryButtonSx,
} from '../../core/pvtaStyles';

interface EventRegistrationBuilderProps {
    eventId: number;
    onDirtyChange?: (dirty: boolean) => void;
}

const SELECTION_LABELS: Record<RegistrationSelectionType, string> = {
    SINGLE: 'genau eine',
    MULTI: 'mindestens eine, mehrere möglich',
};

const move = <T,>(items: T[], from: number, to: number): T[] => {
    if (to < 0 || to >= items.length) {
        return items;
    }
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return next;
};

const trimmed = (value: string): string => value.trim();

/**
 * Einfacher Builder fuer die Zusatzanmeldung (Spec §12): Bereiche und Optionen
 * hinzufuegen, umbenennen, entfernen und per Hoch/Runter sortieren -- bewusst ohne
 * Drag-and-Drop, weil das Projekt dafuer keinen Standard hat.
 */
const EventRegistrationBuilder: React.FC<EventRegistrationBuilderProps> = ({ eventId, onDirtyChange }) => {
    const { showAlert } = useAlert();
    const { registrationForm, registrationAnswerCount, isLoadingRegistrationForm } =
        useEventRegistrationForm(eventId);
    const saveMutation = useSaveEventRegistrationForm();
    const deleteMutation = useDeleteEventRegistrationForm();

    const [enabled, setEnabled] = useState(false);
    const [commentEnabled, setCommentEnabled] = useState(false);
    const [commentLabel, setCommentLabel] = useState('Kommentar / Hinweis');
    const [fields, setFields] = useState<IRegistrationFieldDraft[]>([]);
    const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
    const [pendingAnswerCount, setPendingAnswerCount] = useState(0);
    // Solange ungespeicherte Aenderungen vorliegen, darf der Server-Spiegel-Effekt
    // unten den lokalen Entwurf nicht ueberschreiben -- weder beim initialen Laden
    // eines anderen Formulars noch beim Refetch nach dem Speichern (Muster aus
    // `EventRsvpSection`).
    const [hasLocalChanges, setHasLocalChanges] = useState(false);

    useEffect(() => {
        onDirtyChange?.(hasLocalChanges);
    }, [hasLocalChanges, onDirtyChange]);

    const serverSnapshot = JSON.stringify(registrationForm);
    useEffect(() => {
        if (hasLocalChanges) {
            return;
        }
        const form = JSON.parse(serverSnapshot) as typeof registrationForm;
        setEnabled(Boolean(form));
        setCommentEnabled(form?.comment_enabled ?? false);
        setCommentLabel(form?.comment_label || 'Kommentar / Hinweis');
        setFields(
            (form?.fields ?? []).map((field) => ({
                id: field.id,
                label: field.label,
                selection_type: field.selection_type,
                options: field.options.map((option) => ({ id: option.id, label: option.label })),
            }))
        );
    }, [serverSnapshot, hasLocalChanges]);

    const setEnabledDirty = (value: boolean): void => {
        setHasLocalChanges(true);
        setEnabled(value);
    };

    const setCommentEnabledDirty = (value: boolean): void => {
        setHasLocalChanges(true);
        setCommentEnabled(value);
    };

    const setCommentLabelDirty = (value: string): void => {
        setHasLocalChanges(true);
        setCommentLabel(value);
    };

    const setFieldsDirty = (updater: (previous: IRegistrationFieldDraft[]) => IRegistrationFieldDraft[]): void => {
        setHasLocalChanges(true);
        setFields(updater);
    };

    const updateField = (index: number, patch: Partial<IRegistrationFieldDraft>): void => {
        setFieldsDirty((previous) => previous.map((field, position) => (
            position === index ? { ...field, ...patch } : field
        )));
    };

    const updateOptionLabel = (fieldIndex: number, optionIndex: number, label: string): void => {
        setFieldsDirty((previous) => previous.map((field, position) => (
            position === fieldIndex
                ? {
                    ...field,
                    options: field.options.map((option, optionPosition) => (
                        optionPosition === optionIndex ? { ...option, label } : option
                    )),
                }
                : field
        )));
    };

    const fieldErrors = fields.map((field) => ({
        labelMissing: !trimmed(field.label),
        noOption: field.options.every((option) => !trimmed(option.label)),
        duplicateOption: (() => {
            const labels = field.options.map((option) => trimmed(option.label).toLowerCase()).filter(Boolean);
            return new Set(labels).size !== labels.length;
        })(),
    }));

    const duplicateFieldLabel = (() => {
        const labels = fields.map((field) => trimmed(field.label).toLowerCase()).filter(Boolean);
        return new Set(labels).size !== labels.length;
    })();

    const hasStructureErrors = duplicateFieldLabel
        || fieldErrors.some((entry) => entry.labelMissing || entry.noOption || entry.duplicateOption);

    /**
     * Leere Optionszeilen sollen den Nutzer nicht blockieren, solange mindestens
     * eine Option im Bereich einen Namen hat -- sonst kann er waehrend des Tippens
     * keine neue leere Zeile stehen lassen. Vor dem Senden werden sie deshalb erst
     * jetzt herausgefiltert statt schon im State.
     */
    const buildFieldsPayload = (): IRegistrationFieldDraft[] => fields.map((field) => ({
        ...field,
        options: field.options.filter((option) => trimmed(option.label)),
    }));

    const persist = async (confirmDiscard: boolean): Promise<void> => {
        try {
            await saveMutation.mutateAsync({
                eventId,
                payload: {
                    comment_enabled: commentEnabled,
                    comment_label: commentLabel,
                    fields: buildFieldsPayload(),
                    confirm_discard_answers: confirmDiscard,
                },
            });
            setDiscardDialogOpen(false);
            setHasLocalChanges(false);
            showAlert({ severity: 'success', text: 'Zusatzanmeldung gespeichert.' });
        } catch (error) {
            const parsed = parseBackendError(error);
            const payload = parsed.payload as { code?: string; answer_count?: number } | null;
            if (parsed.status === 409 && payload?.code === 'answers_exist') {
                setPendingAnswerCount(payload.answer_count ?? 0);
                setDiscardDialogOpen(true);
                return;
            }
            showAlert({ severity: 'error', text: parsed.message });
        }
    };

    const handleRemoveForm = async (): Promise<void> => {
        try {
            await deleteMutation.mutateAsync(eventId);
            setHasLocalChanges(false);
            showAlert({ severity: 'success', text: 'Zusatzanmeldung entfernt.' });
        } catch (error) {
            showAlert({ severity: 'error', text: parseBackendError(error).message });
        }
    };

    const canSave = (fields.length > 0 || commentEnabled) && !hasStructureErrors;

    return (
        <Box>
            <FormControlLabel
                control={
                    <Switch
                        checked={enabled}
                        onChange={(event) => setEnabledDirty(event.target.checked)}
                        disabled={isLoadingRegistrationForm}
                    />
                }
                label="Menü / Zusatzanmeldung für diese Veranstaltung"
            />

            {!enabled && registrationForm && (
                <Alert severity="warning" sx={{ mt: 1.5 }}>
                    Die Zusatzanmeldung ist noch gespeichert. „Zusatzanmeldung entfernen"
                    löscht sie samt aller {registrationAnswerCount} bisherigen Auswahlen.
                    <Box sx={{ mt: 1 }}>
                        <Button
                            onClick={() => void handleRemoveForm()}
                            disabled={deleteMutation.isPending}
                            sx={{ ...dangerButtonSx }}
                        >
                            Zusatzanmeldung entfernen
                        </Button>
                    </Box>
                </Alert>
            )}

            {enabled && (
                <Stack spacing={2} sx={{ mt: 2 }}>
                    {fields.map((field, fieldIndex) => (
                        <Box
                            key={field.id ?? `new-field-${fieldIndex}`}
                            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}
                        >
                            <Stack direction="row" spacing={1} alignItems="flex-start">
                                <TextField
                                    label="Bereich"
                                    value={field.label}
                                    onChange={(event) => updateField(fieldIndex, { label: event.target.value })}
                                    size="small"
                                    fullWidth
                                    error={fieldErrors[fieldIndex].labelMissing}
                                    helperText={fieldErrors[fieldIndex].labelMissing
                                        ? 'Bitte einen Namen für den Bereich angeben.'
                                        : undefined}
                                    sx={{ ...fieldSx }}
                                />
                                <TextField
                                    select
                                    label="Auswahl"
                                    value={field.selection_type}
                                    onChange={(event) => updateField(fieldIndex, {
                                        selection_type: event.target.value as RegistrationSelectionType,
                                    })}
                                    size="small"
                                    sx={{ ...fieldSx, minWidth: 220 }}
                                >
                                    {(Object.keys(SELECTION_LABELS) as RegistrationSelectionType[]).map((type) => (
                                        <MenuItem key={type} value={type}>
                                            {SELECTION_LABELS[type]}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <IconButton
                                    aria-label="Bereich nach oben"
                                    onClick={() => setFieldsDirty((previous) => move(previous, fieldIndex, fieldIndex - 1))}
                                    disabled={fieldIndex === 0}
                                    sx={{ minHeight: 44, minWidth: 44 }}
                                >
                                    <ArrowUpwardRoundedIcon />
                                </IconButton>
                                <IconButton
                                    aria-label="Bereich nach unten"
                                    onClick={() => setFieldsDirty((previous) => move(previous, fieldIndex, fieldIndex + 1))}
                                    disabled={fieldIndex === fields.length - 1}
                                    sx={{ minHeight: 44, minWidth: 44 }}
                                >
                                    <ArrowDownwardRoundedIcon />
                                </IconButton>
                                <IconButton
                                    aria-label="Bereich entfernen"
                                    onClick={() => setFieldsDirty((previous) => previous.filter((_item, position) => position !== fieldIndex))}
                                    sx={{ minHeight: 44, minWidth: 44 }}
                                >
                                    <DeleteOutlineRoundedIcon />
                                </IconButton>
                            </Stack>

                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 1, color: GREEN_800 }}>
                                Optionen
                            </Typography>

                            <Stack spacing={1}>
                                {field.options.map((option, optionIndex) => (
                                    <Stack
                                        key={option.id ?? `new-option-${optionIndex}`}
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                    >
                                        <TextField
                                            label={`Option ${optionIndex + 1}`}
                                            value={option.label}
                                            onChange={(event) => updateOptionLabel(fieldIndex, optionIndex, event.target.value)}
                                            size="small"
                                            fullWidth
                                            sx={{ ...fieldSx }}
                                        />
                                        <IconButton
                                            aria-label="Option nach oben"
                                            onClick={() => updateField(fieldIndex, {
                                                options: move(field.options, optionIndex, optionIndex - 1),
                                            })}
                                            disabled={optionIndex === 0}
                                            sx={{ minHeight: 44, minWidth: 44 }}
                                        >
                                            <ArrowUpwardRoundedIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            aria-label="Option nach unten"
                                            onClick={() => updateField(fieldIndex, {
                                                options: move(field.options, optionIndex, optionIndex + 1),
                                            })}
                                            disabled={optionIndex === field.options.length - 1}
                                            sx={{ minHeight: 44, minWidth: 44 }}
                                        >
                                            <ArrowDownwardRoundedIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            aria-label="Option entfernen"
                                            onClick={() => updateField(fieldIndex, {
                                                options: field.options.filter((_item, position) => position !== optionIndex),
                                            })}
                                            sx={{ minHeight: 44, minWidth: 44 }}
                                        >
                                            <DeleteOutlineRoundedIcon fontSize="small" />
                                        </IconButton>
                                    </Stack>
                                ))}
                            </Stack>

                            {fieldErrors[fieldIndex].noOption && (
                                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                                    Mindestens eine Option mit Namen angeben.
                                </Typography>
                            )}
                            {fieldErrors[fieldIndex].duplicateOption && (
                                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                                    Die Optionen dieses Bereichs müssen unterschiedlich heißen.
                                </Typography>
                            )}

                            <Button
                                startIcon={<AddRoundedIcon />}
                                onClick={() => updateField(fieldIndex, {
                                    options: [...field.options, { label: '' }],
                                })}
                                sx={{ ...secondaryButtonSx, mt: 1 }}
                            >
                                Option hinzufügen
                            </Button>
                        </Box>
                    ))}

                    <Button
                        startIcon={<AddRoundedIcon />}
                        onClick={() => setFieldsDirty((previous) => [
                            ...previous,
                            { label: '', selection_type: 'SINGLE', options: [{ label: '' }] },
                        ])}
                        sx={{ ...secondaryButtonSx }}
                    >
                        Bereich hinzufügen
                    </Button>

                    <FormControlLabel
                        control={
                            <Switch
                                checked={commentEnabled}
                                onChange={(event) => setCommentEnabledDirty(event.target.checked)}
                            />
                        }
                        label="Kommentarfeld anbieten (z. B. für Allergien)"
                    />

                    {commentEnabled && (
                        <TextField
                            label="Beschriftung des Kommentarfelds"
                            value={commentLabel}
                            onChange={(event) => setCommentLabelDirty(event.target.value)}
                            size="small"
                            fullWidth
                            sx={{ ...fieldSx }}
                        />
                    )}

                    {duplicateFieldLabel && (
                        <Alert severity="error">
                            Die Bereiche müssen unterschiedlich heißen.
                        </Alert>
                    )}

                    <Button
                        onClick={() => void persist(false)}
                        disabled={saveMutation.isPending || !canSave}
                        sx={{ ...primaryButtonSx }}
                    >
                        Zusatzanmeldung speichern
                    </Button>
                </Stack>
            )}

            <Dialog open={discardDialogOpen} onClose={() => setDiscardDialogOpen(false)}>
                <DialogTitle>Bisherige Auswahlen löschen?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Für diese Veranstaltung liegen bereits {pendingAnswerCount} Auswahlen vor.
                        Wenn du die Menüstruktur änderst, werden die bisherigen Auswahlen gelöscht
                        und die Teilnehmer müssen ihre Angaben erneut machen.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDiscardDialogOpen(false)} sx={{ ...secondaryButtonSx }}>
                        Abbrechen
                    </Button>
                    <Button
                        onClick={() => void persist(true)}
                        disabled={saveMutation.isPending}
                        sx={{ ...dangerButtonSx }}
                    >
                        Trotzdem ändern
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default EventRegistrationBuilder;
